import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useWebSocket } from './useWebSocket';
import type { WSEvent } from '@/lib/ws';

/**
 * Subscribes to WebSocket events and invalidates OS-related queries in real-time.
 * Use in pages that display service order data (list, detail, etc.).
 */
export function useOSRealtime(workshopId: string | null) {
  const queryClient = useQueryClient();

  const handleWSEvent = useCallback(
    (event: WSEvent) => {
      if (event.event === 'os-status-changed') {
        queryClient.invalidateQueries({ queryKey: ['service-orders'] });
        queryClient.invalidateQueries({ queryKey: ['dashboard'] });
        queryClient.invalidateQueries({ queryKey: ['service-order', event.data.osId] });
        queryClient.invalidateQueries({ queryKey: ['audit-log', event.data.osId] });
      }
      if (event.event === 'os-stage-changed') {
        // O dashboard também: mudar de etapa é movimento, e a OS sai das paradas.
        queryClient.invalidateQueries({ queryKey: ['dashboard'] });
        queryClient.invalidateQueries({ queryKey: ['service-orders'] });
        queryClient.invalidateQueries({ queryKey: ['service-order', event.data.osId] });
        queryClient.invalidateQueries({ queryKey: ['audit-log', event.data.osId] });
      }
      if (event.event === 'os-deleted') {
        // Lista e dashboard perdem a OS; o detalhe aberto recarrega e mostra o 404.
        queryClient.invalidateQueries({ queryKey: ['service-orders'] });
        queryClient.invalidateQueries({ queryKey: ['dashboard'] });
        queryClient.invalidateQueries({ queryKey: ['service-order', event.data.osId] });
      }
      // Vaga trocada (ou liberada) por outra pessoa: o mapa e "Carros na
      // oficina" leem de `service-orders`.
      if (event.event === 'slot-status-changed') {
        queryClient.invalidateQueries({ queryKey: ['service-orders'] });
        queryClient.invalidateQueries({ queryKey: ['slots-status'] });
        if (event.data.osId) queryClient.invalidateQueries({ queryKey: ['service-order', event.data.osId] });
      }
      if (event.event === 'os-opened') {
        queryClient.invalidateQueries({ queryKey: ['service-orders'] });
        queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      }
      if (event.event === 'quote-updated') {
        queryClient.invalidateQueries({ queryKey: ['os-quotes', event.data.osId] });
        queryClient.invalidateQueries({ queryKey: ['service-order', event.data.osId] });
      }
      if (event.event === 'new-media') {
        queryClient.invalidateQueries({ queryKey: ['os-media', event.data.osId] });
      }
    },
    [queryClient],
  );

  useWebSocket({ workshopId, onEvent: handleWSEvent });
}
